import { subYears } from "date-fns";
import { z } from "zod";

import {
  TEXT_EXTRA_SHORT_LENGTH,
  TEXT_MEDIUM_LENGTH,
} from "~/common/constants";
import {
  MultiCheckOptions,
  RadioGroupOptions,
} from "~/common/utils/consultations/shared";
import { emergencyContactSchema } from "~/patients/utils";
import {
  dateSchema,
  extractMultiCheckValues,
} from "~/patients/utils/consultations";
import { extractRadioGroupValues } from "../intake/symptoms/extractRadioGroupValues";

export const createRadioButtonSchema = <
  T extends readonly [string, ...string[]],
>(
  options: T,
) => {
  return z.object({
    answer: z.enum(options, {
      error: "Please select an answer to continue.",
    }),
  });
};

export const createMultiCheckSchema = (
  options: readonly MultiCheckOptions[],
) => {
  const values = extractMultiCheckValues(options);

  const valuesSchema = z.array(
    z.object({
      value: z.enum(values),
      extra_data: z.string().optional(),
    }),
  );

  return z
    .object({
      answer: z.object({
        values: valuesSchema,
        extra_data: z.string().optional(),
      }),
    })
    .refine(
      (data) => {
        const commonExtraDataRequired = data.answer.values.some(
          (value) =>
            options.find((option) => option.value === value.value)
              ?.asksCommonExtraData,
        );

        const hasCommonExtraData =
          commonExtraDataRequired && !data.answer.extra_data;

        return !hasCommonExtraData;
      },
      {
        message: "Please provide a valid justification.",
        path: ["answer", "extra_data"],
      },
    );
};

export const medicationSchema = z.object({
  name: z
    .string()
    .min(1, "Please enter medication name.")
    .max(
      TEXT_EXTRA_SHORT_LENGTH,
      `Please provide a name less than ${TEXT_EXTRA_SHORT_LENGTH} characters`,
    ),
  dose: z
    .string()
    .min(1, "Please specify dose.")
    .max(
      TEXT_MEDIUM_LENGTH,
      `Please provide a dose less than ${TEXT_MEDIUM_LENGTH} characters`,
    ),
  frequency: z
    .string()
    .min(1, "Please specify frequency.")
    .max(
      TEXT_MEDIUM_LENGTH,
      `Please provide a frequency less than ${TEXT_MEDIUM_LENGTH} characters`,
    ),
});

export const pastMedicationSchema = z.object({
  name: z
    .string()
    .min(1, "Please enter medication name.")
    .max(
      TEXT_EXTRA_SHORT_LENGTH,
      `Please provide a name less than ${TEXT_EXTRA_SHORT_LENGTH} characters`,
    ),
});

export const listMedicationSchema = z.object({
  answer: z.object({ medications: z.array(medicationSchema) }),
});

export const listPastMedicationSchema = z.object({
  answer: z.object({ medications: z.array(pastMedicationSchema) }),
});

const today = new Date();

export const dateAdultRequiredSchema = dateSchema.extend({
  date: dateSchema.shape.date.refine(
    (val) => {
      const date = new Date(val);
      return !isNaN(date.getTime()) && date <= subYears(today, 18);
    },
    {
      message: "You must be at least 18 years old.",
    },
  ),
});

export const dateAdultRequiredFormSchema = z.object({
  answer: dateAdultRequiredSchema,
});

export type ScreeningDateType = z.infer<typeof dateAdultRequiredSchema>;

export const createRadioGroupSchema = (
  options: readonly RadioGroupOptions[],
) => {
  const values = extractRadioGroupValues(options);
  const valuesSchema = z.array(
    z.object({
      value: z.enum(values, {
        error: "Please select an option",
      }),
    }),
  );

  return z.object({
    answer: z.object({
      values: valuesSchema,
    }),
  });
};

export const emergencyContactFormSchema = z.object({
  answer: emergencyContactSchema,
});

export type EmergencyContactFormValues = z.infer<
  typeof emergencyContactFormSchema
>;
